import { z } from '@awpmarine/core/zod';
import { Spinner } from '@awpmarine/ui/components/ui/spinner';
import { ParaglideMessage } from '@inlang/paraglide-js-react';
import { Link } from '@tanstack/react-router';
import { CheckIcon } from 'lucide-react';
import * as React from 'react';

import { submitWith, useSchemaForm, useSelector } from '@/components/form';
import { client } from '@/lib/api';
import { m } from '@/paraglide/messages';

/** The newsletter sign-up, styled for the navy footer band. */
export function NewsletterForm() {
  const [subscribed, setSubscribed] = React.useState(false);

  const formSchema = z.object({
    email: z.email(m.newsletter_email_invalid()),
    consent: z.boolean().refine((value) => value, { message: m.newsletter_consent_required() }),
  });
  const form = useSchemaForm({
    schema: formSchema,
    defaultValues: { email: '', consent: false },
    onSubmitAsync: async ({ value }) => {
      try {
        await client.marketing.newsletter(value.email);
      } catch {
        return m.newsletter_failed();
      }
    },
    onSubmit: () => setSubscribed(true),
  });
  const isSubmitting = useSelector(form.store, (state) => state.isSubmitting);
  // One line under the band holds whichever problem comes first.
  const error = useSelector(
    form.store,
    (state) =>
      state.fieldMeta.email?.errors[0]?.message ??
      state.fieldMeta.consent?.errors[0]?.message ??
      (typeof state.errorMap.onSubmit === 'string' ? state.errorMap.onSubmit : undefined),
  );

  if (subscribed) {
    return (
      <p className='text-on-navy flex items-center gap-2'>
        <CheckIcon className='size-5 text-white' />
        {m.newsletter_subscribed()}
      </p>
    );
  }

  return (
    <form method='post' onSubmit={submitWith(form)} className='flex w-full max-w-115 flex-col gap-3'>
      <div className='flex flex-wrap items-end gap-2.5'>
        <form.Field name='email'>
          {(field) => (
            <label className='text-on-navy flex min-w-60 flex-1 flex-col gap-1.5 text-sm'>
              {m.newsletter_email()}
              <input
                name={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(event) => field.handleChange(event.target.value)}
                type='email'
                autoComplete='email'
                placeholder={m.newsletter_email_placeholder()}
                aria-invalid={!field.state.meta.isValid}
                className='text-ink min-h-12.5 rounded-full bg-white px-4.5 text-base outline-none focus-visible:ring-2 focus-visible:ring-[#9cc3f5]'
              />
            </label>
          )}
        </form.Field>
        <button
          type='submit'
          disabled={isSubmitting}
          className='text-navy flex min-h-12.5 items-center justify-center rounded-full bg-white px-6.5 font-semibold transition-opacity hover:opacity-90 disabled:opacity-70'
        >
          {isSubmitting ? <Spinner /> : m.newsletter_subscribe()}
        </button>
      </div>
      <form.Field name='consent'>
        {(field) => (
          <label className='text-on-navy flex items-start gap-2.5 text-sm'>
            <input
              type='checkbox'
              name={field.name}
              checked={field.state.value}
              onBlur={field.handleBlur}
              onChange={(event) => field.handleChange(event.target.checked)}
              className='mt-0.5 size-4.5 shrink-0 accent-white'
            />
            <span>
              <ParaglideMessage
                message={m.newsletter_consent}
                inputs={{}}
                markup={{
                  link: ({ children }) => (
                    <Link
                      to='/legal/$slug'
                      params={{ slug: 'privacy' }}
                      className='underline underline-offset-4 hover:text-white'
                    >
                      {children}
                    </Link>
                  ),
                }}
              />
            </span>
          </label>
        )}
      </form.Field>
      {error && (
        <p role='alert' className='text-sm text-[#ffb4a8]'>
          {error}
        </p>
      )}
    </form>
  );
}
